import React, { useEffect } from "react";
import { Separator } from "../../ui/separator";

const ChildPrivacy  = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="flex-grow bg-white py-8 md:py-12">
      <div className="container mx-auto px-2 md:px-4">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-8 md:mb-12">
            <h1 className="text-3xl font-bold mb-6 text-center text-[#523880]">
              Children's Privacy / COPPA Notice
            </h1>
            <p className="text-sm text-gray-500 text-center mb-8">
              Last updated: Jun 02, 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-base leading-relaxed text-gray-800 mb-4">
              Oye Creators ("we," "us," "Platform") is committed to protecting
              the privacy of all users—including minors—who access our
              marketplace for content creation services. This notice explains
              how we handle information relating to children and minors under
              sixteen (16) years of age, in compliance with U.S. COPPA, the EU
              GDPR, India's Information Technology Act, 2000 (and the
              forthcoming DPDP Act), and other applicable laws.
            </p>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#EE3788]">
                1. Applicability
              </h2>
              <ul className="list-disc ml-6 space-y-2">
                <li className="text-base leading-relaxed text-gray-800">
                  Who it covers: Anyone under 16 years old who attempts to
                  register for, access, or use the Platform.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Scope: Applies to all personal information collected
                  directly or indirectly from minors under 16, whether in the
                  U.S., India, the EU, or elsewhere.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#EE3788]">
                2. Information We May Collect
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  Account details: Name, email address, date of birth (to
                  verify age).
                </li>
                <li>
                  Usage data: Pages visited, clicks, time stamps, device
                  information.
                </li>
                <li>
                  Communications: Messages sent through our Platform's
                  messaging tools.
                </li>
              </ul>
              <p className="mb-4">
                Note: We do not knowingly collect other personal information
                (e.g., photographs, financial data) from anyone we know to be
                under 16 without verifiable parental consent.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#EE3788]">
                3. Parental Consent Requirements
              </h2>
              <p className="mb-3">
                <strong>U.S. (COPPA):</strong>
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  Before collecting or using personal information from a child
                  under 13, we must obtain verifiable parental consent.
                </li>
                <li>
                  Parents have the right to review the information, revoke
                  consent, and request deletion.
                </li>
              </ul>

              <p className="mb-3">
                <strong>EU (GDPR):</strong>
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  Where EU law applies, individuals under 16 require parental
                  consent for processing their personal data. We comply with
                  the stricter setting of 16.
                </li>
              </ul>

              <p className="mb-3">
                <strong>India:</strong>
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  Under the Information Technology Act and proposed DPDP
                  rules, data fiduciaries must obtain parental or guardian
                  consent for processing personal data of children (under 18).
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#EE3788]">
                4. How to Provide or Revoke Consent
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>Provision:</strong> Parents can provide consent by
                  completing a signed consent form and verifying identifiers
                  as required under COPPA.
                </li>
                <li>
                  <strong>Revocation:</strong> Parents may revoke consent at
                  any time by contacting our DPO at privacy@oyecreators.com.
                  Upon revocation, we will delete any previously collected
                  data, unless otherwise required by law.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#EE3788]">
                5. Use and Disclosure of Children's Data
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>Limited use:</strong> We use children's personal
                  information solely to operate the Platform (e.g., user
                  authentication, delivering services).
                </li>
                <li>
                  <strong>No marketing:</strong> We do not use children's data
                  for marketing, advertising, or profiling purposes.
                </li>
                <li>
                  <strong>Third parties:</strong> We will not share children's
                  information with any third party—except our service
                  providers under strict confidentiality—or as required by
                  law.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#EE3788]">
                6. Security Measures
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>Technical safeguards:</strong> Encryption of data in
                  transit (TLS) and at rest, regular security audits, access
                  controls.
                </li>
                <li>
                  <strong>Organizational safeguards:</strong> Training of
                  staff, strict internal policies, and periodic reviews.
                </li>
                <li>
                  <strong>Breach protocol:</strong> In case of data breach, we
                  will notify affected parents/guardians and relevant
                  authorities.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#EE3788]">
                7. Rights of Parents and Guardians
              </h2>
              <p className="mb-3">Parents may:</p>
              <ol className="list-decimal pl-5 mb-4 space-y-2">
                <li>
                  Review personal information collected about their child.
                </li>
                <li>Request deletion or correction of that information.</li>
                <li>Revoke consent at any time.</li>
                <li>Limit future collection of their child's data.</li>
              </ol>

              <p className="mb-3">To exercise these rights, contact:</p>
              <p className="mb-1">Data Protection Officer</p>
              <p className="mb-1">TTL Media Private Limited (Oye Creators)</p>
              <p className="mb-1">Quantum Works, CTS No. 177 p, metro station, S. No. 43/2, near Nal stop, Pandurang Colony, Erandwane, Pune, Maharashtra 411004</p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#EE3788]">
                8. Prohibited Conduct & Enforcement
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>No under-16 access without consent:</strong>{" "}
                  Accounts of users under 16 will be suspended or deleted if
                  registered without consent.
                </li>
                <li>
                  <strong>Legal recourse:</strong> Violations may result in
                  action under COPPA, GDPR, Indian IT law, and other
                  jurisdictions.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#EE3788]">
                9. Indemnification & Disclaimer
              </h2>
              <p className="mb-4">
                Brands, Creators, and Parents/Guardians agree to indemnify and
                hold harmless Oye Creators, its affiliates, officers,
                employees, and agents against any claims, losses, or
                liabilities arising from failure to obtain required consents,
                misrepresentation of age, or unauthorized access to a minor's
                account.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#EE3788]">
                Contact
              </h2>
              <p className="mb-3">
                For questions or to provide/revoke consent, contact:
              </p>
              <p className="mb-1">Email: privacy@oyecreators.com</p>
              <p className="mb-1">Phone: +91-8484834105</p>
              <p className="mb-1">
                Address: TTL Media Private Limited, Quantum Works, CTS No. 177 p, metro station, S. No. 43/2, near Nal stop, Pandurang Colony, Erandwane, Pune, Maharashtra 411004
              </p>
            </section>
          </div>
        </div>
      </div>
    </main>
  )
}

export default ChildPrivacy
